import { useEffect, useState } from 'react';
import { AssistantMessageDto, ConversationMessageDto, TextMessageContent } from '@endion/types';
import isMobile from 'is-mobile';
import { nanoid } from 'nanoid';
import { useImmer } from 'use-immer';
import { sendChatMessage } from '../queries/chat-message.query';
import { readDataStream } from '../utils/read-data-stream';
import { useCustomerChatbotContext } from './use-customer-chatbot-context';
import { useInitialMessage } from './use-initial-message';

export function useChat({
  conversationHistory,
  parseApiError,
  inputRef,
}: {
  conversationHistory: ConversationMessageDto[] | undefined;
  parseApiError: (error: Error | string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const { initialMessage } = useInitialMessage();
  const defaultAssistantMessage: AssistantMessageDto = {
    content: [
      {
        type: 'text',
        text: initialMessage,
      },
    ],
    role: 'assistant' as const,
    id: nanoid(7),
    createdAt: new Date().toISOString(),
  };
  const [messages, setMessages] = useImmer<ConversationMessageDto[]>([defaultAssistantMessage]);
  const [input, setInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { chatbotApiKey, websiteProvidedContext } = useCustomerChatbotContext();

  // set either the initial message or the conversation history as the initial messages
  useEffect(() => {
    if (conversationHistory && conversationHistory.length > 0) {
      setMessages(conversationHistory);
    } else {
      setMessages([defaultAssistantMessage]);
    }
  }, [initialMessage, conversationHistory]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value);
  }

  async function submit(messages: ConversationMessageDto[]) {
    setIsLoading(true);

    try {
      const { reader, backendIncomingMessageId } = await sendChatMessage({
        // Send all messages, including the new one
        messages,
        chatbotApiKey,
        context: websiteProvidedContext,
      });

      // Create a new message for the assistant's response
      const assistantMessage: AssistantMessageDto = {
        content: [
          {
            type: 'text',
            text: '',
          },
        ],
        role: 'assistant',
        id: backendIncomingMessageId,
        createdAt: new Date().toISOString(),
      };

      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages, assistantMessage];
        return updatedMessages;
      });

      const chatReader = readDataStream({ reader });
      for await (const { type, value } of chatReader) {
        switch (type) {
          case 'STRING': {
            setMessages((prevMessages) => {
              const lastMessage = prevMessages.at(-1);
              if (!lastMessage) throw new Error('No message to update');
              (lastMessage.content[0] as TextMessageContent).text += value;
            });
            break;
          }
          case 'TOOLCALL': {
            setMessages((prevMessages) => {
              const lastMessage = prevMessages.at(-1);
              if (!lastMessage || lastMessage.role !== 'assistant')
                throw new Error('No message to update or not an assistant message');
              (lastMessage.toolInvocations || (lastMessage.toolInvocations = [])).push(value);
            });
            break;
          }
          case 'TOOLRESULT': {
            setMessages((prevMessages) => {
              const lastMessage = prevMessages.at(-1);
              if (!lastMessage || lastMessage.role !== 'assistant')
                throw new Error('No message to update or not an assistant message');
              const toolInvocations = lastMessage.toolInvocations;
              if (!toolInvocations || toolInvocations.length === 0)
                throw new Error(`Received tool result without tool call, ${value}`);
              const toolCallToUpdate = toolInvocations.filter(
                (toolCall) => toolCall.toolCallId === value.toolCallId,
              )[0];
              if (!toolCallToUpdate) throw new Error(`Tool call not found for tool result, ${value}`);
              toolCallToUpdate.result = value.result;
            });
            break;
          }
          case 'ERROR': {
            parseApiError(value);
            // should remove the assistant message
            setMessages((prevMessages) => {
              prevMessages.pop();
            });
            break;
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        parseApiError(error);
      } else {
        // TODO: we should handle the error in the UI
        console.error('Error sending chat message:', error);
      }
    } finally {
      setIsLoading(false);
      if (!isMobile({ tablet: true, featureDetect: true })) {
        setTimeout(() => inputRef.current?.focus(), 10);
      }
    }
  }

  async function append(userMessage: ConversationMessageDto) {
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    submit(updatedMessages);
  }

  return {
    messages,
    input,
    setInput,
    handleInputChange,
    append,
    isLoading,
    setIsLoading,
    setMessages,
    defaultAssistanMessage: defaultAssistantMessage,
    submit,
  };
}
